import React, { useEffect, useState } from "react";
import { connect } from "react-redux";
import { setLoading, setSnackInfo } from "../redux/actions/appAction";
import {
  Button,
  Box,
  Paper,
  Typography,
  CircularProgress,
  Divider,
  IconButton,
  Collapse,
  LinearProgress,
} from "@material-ui/core";
import CheckCircleIcon from "@material-ui/icons/CheckCircle";
import CancelIcon from "@material-ui/icons/Cancel";
import AssignmentIcon from "@material-ui/icons/Assignment";
import ExpandMoreIcon from "@material-ui/icons/ExpandMore";
import ExpandLessIcon from "@material-ui/icons/ExpandLess";
import ChevronLeftIcon from "@material-ui/icons/ChevronLeft";
import ChevronRightIcon from "@material-ui/icons/ChevronRight";
import HttpService from "../services/httpService";
import { useSelector } from "react-redux";

const PAGE_SIZE = 20;

const ACTIVITY_ROW_HEADERS = [
  "Emp ID",
  "Department",
  "City",
  "EC",
  "Role",
  "Name",
  "Email ID",
  "Joining Date",
  "Status",
  "Status Note",
  "Recruiter Name",
  "Needs Review",
  "L1 Manager Name",
  "L1 Manager Email ID",
  "L1 Manager Designation",
  "L2 Manager Name",
  "L2 Manager Email ID",
  "L2 Manager Role",
  "L3 Manager Name",
  "L3 Manager Email ID",
  "L3 Manager Role",
  "L4 Manager Name",
  "L4 Manager Email ID",
  "L4 Manager Role",
  "L5 Manager Name",
  "L5 Manager Email ID",
  "L5 Manager Role",
];

function getActivityRawText(activity) {
  const raw = activity?.activity ?? activity?.comment ?? "";
  return typeof raw === "string" ? raw.trim() : "";
}

function parseActivityRowFields(activity) {
  const raw = getActivityRawText(activity);
  if (!raw) return [];

  const delimiter = raw.includes("\t") ? "\t" : raw.includes("|") ? "|" : null;
  if (!delimiter) return [{ label: "Activity", value: raw }];

  return raw
    .split(delimiter)
    .map((value, index) => ({
      label: ACTIVITY_ROW_HEADERS[index] || `Column ${index + 1}`,
      value: String(value ?? "").trim() || "—",
    }))
    .filter((field) => field.value !== "—");
}

function HrRacksheetActivityLogDashboard(props) {
  const userDetails = useSelector((state) => state.user?.userDetails ?? {});
  const userReady = Boolean(userDetails?._id);

  const urlParams = new URLSearchParams(props.location?.search || "");
  const apiEmpId = urlParams.get("empId") || "";
  const apiAction = urlParams.get("action") || "";
  const [activitiesLoading, setActivitiesLoading] = useState(false);
  const [activities, setActivities] = useState([]);
  const [page, setPage] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [expandedId, setExpandedId] = useState({});

  const parseActivityList = (payload) => {
    if (!payload) return [];
    const raw = payload.data !== undefined ? payload.data : payload;
    if (Array.isArray(raw)) return raw;
    if (Array.isArray(raw?.activities)) return raw.activities;
    if (Array.isArray(raw?.rows)) return raw.rows;
    if (Array.isArray(raw?.list)) return raw.list;
    if (Array.isArray(raw?.data) && Array.isArray(raw.data[0]?.activities)) {
      return raw.data[0].activities || [];
    }
    return [];
  };

  const parseTotalCount = (payload, listLength, pageNum, limit) => {
    if (!payload || typeof payload !== "object") {
      return listLength;
    }
    const asNum = (v) => {
      if (typeof v === "number" && !Number.isNaN(v)) return v;
      if (typeof v === "string" && v.trim() !== "") {
        const n = parseInt(v, 10);
        return Number.isNaN(n) ? null : n;
      }
      return null;
    };
    /** Prefer `pagination.totalCount` — not root `count` (often rows per page, e.g. 10) */
    const candidates = [
      payload.pagination?.totalCount,
      payload.pagination?.total,
      payload.totalCount,
      payload.total,
      payload.data?.totalCount,
      payload.data?.total,
      payload.meta?.total,
    ];
    for (const c of candidates) {
      const n = asNum(c);
      if (n != null && n >= 0) return n;
    }
    if (listLength < limit) {
      return (pageNum - 1) * limit + listLength;
    }
    return pageNum * limit + 1;
  };

  useEffect(() => {
    // Reset pagination when filters change.
    setPage(1);
    setExpandedId({});
  }, [apiEmpId, apiAction]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setActivitiesLoading(true);
      try {
        const response = await HttpService.getHrRackLogsGetAll({
          page,
          limit: PAGE_SIZE,
          empId: apiEmpId,
          action: apiAction,
        });
        const payload = response?.data;
        const list = parseActivityList(payload);
        const total = parseTotalCount(payload, list.length, page, PAGE_SIZE);
        if (!cancelled) {
          setActivities(list);
          setTotalCount(total);
        }
      } catch (error) {
        if (!cancelled) {
          props.setSnackInfo(
            error?.response?.data?.message ||
              "Error fetching activity logs",
            "error"
          );
          setActivities([]);
          setTotalCount(0);
        }
      } finally {
        if (!cancelled) setActivitiesLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [page, apiEmpId, apiAction]);

  const formatDateTime = (value) => {
    if (!value) return "-";
    return new Date(value).toLocaleString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "numeric",
      minute: "2-digit",
    });
  };

  /** Show org email consistently (API may still return @naraci.com) */
  const formatDisplayEmail = (email) => {
    if (!email || typeof email !== "string") return "";
    return email.replace(/@naraci\.com$/i, "@narachi.com");
  };

  const totalPages = Math.max(1, Math.ceil(totalCount / PAGE_SIZE));
  const safePage = Math.min(page, totalPages);
  const pageOffset = (safePage - 1) * PAGE_SIZE;

  const toggleExpand = (id) => {
    setExpandedId((prev) => ({
      ...prev,
      [id]: !(prev[id] !== false),
    }));
  };

  const getActivityMeta = (activity) => {
    const action = activity.action;
    if (action === "approve") {
      return {
        title: "Approval Activity",
        Icon: CheckCircleIcon,
        iconColor: "#2e7d32",
        iconBg: "rgba(76, 175, 80, 0.12)",
      };
    }
    if (action === "reject") {
      return {
        title: "Rejection Activity",
        Icon: CancelIcon,
        iconColor: "#c62828",
        iconBg: "rgba(244, 67, 54, 0.12)",
      };
    }
    return {
      title: "HR Racksheet Activity",
      Icon: AssignmentIcon,
      iconColor: "#1565c0",
      iconBg: "rgba(33, 150, 243, 0.12)",
    };
  };

  if (!userReady) {
    return (
      <div style={{ padding: "0 16px", backgroundColor: "#f5f5f5", minHeight: "100vh" }}>
        <Box display="flex" flexDirection="column" alignItems="center" justifyContent="center" p={6} minHeight={320}>
          <CircularProgress />
          <Typography variant="body2" color="textSecondary" style={{ marginTop: 16 }}>
            Loading your session…
          </Typography>
        </Box>
      </div>
    );
  }

  return (
    <div style={{ padding: "0 16px", backgroundColor: "#f5f5f5", minHeight: "100vh" }}>
      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        flexWrap="wrap"
        gap={1}
        pt={2}
        pb={1}
      >
        <Typography variant="h5" style={{ fontWeight: 600, color: "#263238" }}>
          HR Racksheet activity log
        </Typography>
        <Button
          variant="outlined"
          size="small"
          onClick={() => props.history.push("/roster")}
        >
          Go to HR Racksheets
        </Button>
      </Box>

        <>
          {activitiesLoading && (
            <Box mt={1} mb={0}>
              <LinearProgress />
            </Box>
          )}
          {totalCount > 0 && (
            <Box
              display="flex"
              justifyContent="center"
              alignItems="center"
              gap={1}
              mb={2}
              mt={2}
              flexWrap="wrap"
            >
              <IconButton
                size="small"
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                disabled={safePage <= 1 || activitiesLoading}
                aria-label="Previous page"
                title="Previous page"
                disableRipple
                style={{
                  padding: 8,
                  color: safePage <= 1 || activitiesLoading ? "#bdbdbd" : "#212121",
                }}
              >
                <ChevronLeftIcon style={{ fontSize: 22 }} />
              </IconButton>
              <Box
                display="flex"
                alignItems="center"
                justifyContent="center"
                gap={0.5}
                mx={1}
                style={{
                  maxWidth: "100%",
                  overflowX: "auto",
                  flexWrap: "nowrap",
                }}
              >
                {totalPages > 40 ? (
                  <Typography
                    variant="body2"
                    style={{
                      color: "#212121",
                      minWidth: 72,
                      textAlign: "center",
                      fontWeight: 500,
                    }}
                  >
                    {safePage} / {totalPages}
                  </Typography>
                ) : (
                  Array.from({ length: totalPages }, (_, i) => i + 1).map((num) => {
                    const active = num === safePage;
                    return (
                      <Box
                        key={num}
                        component="button"
                        type="button"
                        disabled={activitiesLoading}
                        onClick={() => !activitiesLoading && setPage(num)}
                        aria-label={`Page ${num}`}
                        aria-current={active ? "page" : undefined}
                        style={{
                          minWidth: 32,
                          width: 32,
                          height: 32,
                          borderRadius: "50%",
                          border: "none",
                          backgroundColor: active ? "#e8e8e8" : "transparent",
                          color: "#212121",
                          fontWeight: active ? 600 : 400,
                          fontSize: 14,
                          fontFamily: "inherit",
                          cursor: activitiesLoading ? "default" : "pointer",
                          display: "inline-flex",
                          alignItems: "center",
                          justifyContent: "center",
                          padding: 0,
                          flexShrink: 0,
                          outline: "none",
                          transition: "background-color 0.15s ease",
                        }}
                      >
                        {num}
                      </Box>
                    );
                  })
                )}
              </Box>
              <IconButton
                size="small"
                onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                disabled={safePage >= totalPages || activitiesLoading}
                aria-label="Next page"
                title="Next page"
                disableRipple
                style={{
                  padding: 8,
                  color: safePage >= totalPages || activitiesLoading ? "#bdbdbd" : "#212121",
                }}
              >
                <ChevronRightIcon style={{ fontSize: 22 }} />
              </IconButton>
            </Box>
          )}

          {activities.length === 0 ? (
            <Paper
              elevation={0}
              style={{
                padding: 24,
                textAlign: "center",
                border: "1px solid #e0e0e0",
                borderRadius: 8,
                marginTop: 16,
              }}
            >
              <Typography variant="body2" color="textSecondary">
                No activities found.
              </Typography>
            </Paper>
          ) : (
            <Box display="flex" flexDirection="column" gap={2} mt={2} pb={3}>
              {activities.map((activity, index) => {
                const globalIndex = pageOffset + index;
                const meta = getActivityMeta(activity);
                const IconComp = meta.Icon;
                const id = activity._id || globalIndex;
                const isOpen = expandedId[id] !== false;

                const createdBy = activity?.createdBy || activity?.tableId?.createdBy;
                const updatedBy = activity?.updatedBy || activity?.tableId?.updatedBy;
                const roster = activity?.rosterId;
                const activityFields = parseActivityRowFields(activity);
                const empIdField = activityFields.find((field) => field.label === "Emp ID");
                const displayEmpId =
                  activity?.empId || roster?.empId || empIdField?.value || "";
                const excelS3Url = activity?.excelS3Url;
                const excelFileName = activity?.excelFileName;
                const activityText = getActivityRawText(activity) || "—";
                const createdAt = activity?.createdAt || activity?.tableId?.createdAt;
                const updatedAt = activity?.updatedAt || activity?.tableId?.updatedAt;

                return (
                  <Paper
                    key={id}
                    elevation={0}
                    style={{
                      border: "1px solid #e0e0e0",
                      borderRadius: 8,
                      overflow: "hidden",
                      backgroundColor: "#fff",
                    }}
                  >
                    <Box
                      display="flex"
                      alignItems="center"
                      px={2}
                      py={1.5}
                      style={{ cursor: "pointer" }}
                      onClick={() => toggleExpand(id)}
                    >
                      <Box
                        style={{
                          width: 40,
                          height: 40,
                          borderRadius: 8,
                          backgroundColor: meta.iconBg,
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          marginRight: 12,
                          flexShrink: 0,
                        }}
                      >
                        <IconComp style={{ color: meta.iconColor, fontSize: 22 }} />
                      </Box>
                      <Box flex={1} minWidth={0}>
                        <Typography variant="subtitle1" style={{ fontWeight: 700 }}>
                          {displayEmpId || "—"}
                        </Typography>
                      </Box>
                      {/* <IconButton
                        size="small"
                        onClick={(e) => {
                          e.stopPropagation();
                          toggleExpand(id);
                        }}
                        aria-label="Expand"
                      >
                        {isOpen ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                      </IconButton> */}
                    </Box>

                    <Collapse in={isOpen}>
                      <Divider />
                      <Box px={2} py={2}>
                        {activityFields.length > 1 ? (
                          <Box display="flex" flexDirection="column" style={{ gap: 6 }}>
                            <Typography variant="body2" style={{ fontWeight: 700, color: "#37474f" }}>
                              Activity
                            </Typography>
                            {activityFields.map((field) => (
                              <Typography
                                key={`${id}-${field.label}`}
                                variant="body2"
                                style={{ lineHeight: 1.6, color: "#37474f" }}
                              >
                                <strong>{field.label}:</strong> {field.value}
                              </Typography>
                            ))}
                          </Box>
                        ) : (
                          <Typography
                            variant="body2"
                            style={{ lineHeight: 1.7, color: "#37474f", whiteSpace: "pre-wrap" }}
                          >
                            <strong>Activity:</strong> {activityText}
                          </Typography>
                        )}
                        {roster && typeof roster === "object" ? (
                          <Box mt={2} display="flex" flexDirection="column" style={{ gap: 6 }}>
                            {roster.name ? (
                              <Typography variant="body2">
                                <strong>Roster Name:</strong> {roster.name}
                              </Typography>
                            ) : null}
                            {roster.emailId ? (
                              <Typography variant="body2">
                                <strong>Roster Email:</strong> {roster.emailId}
                              </Typography>
                            ) : null}
                            {roster.department ? (
                              <Typography variant="body2">
                                <strong>Roster Department:</strong> {roster.department}
                              </Typography>
                            ) : null}
                          </Box>
                        ) : null}
                        <Box mt={2} display="flex" flexDirection="column" gap={1}>
                          {(() => {
                            const action = String(activity?.action || "").toLowerCase();
                            const isEdit = action === "edit" || action === "update";
                            const actor = isEdit ? updatedBy : createdBy;
                            return (
                              <Typography variant="body2">
                                <strong>{isEdit ? "Updated by:" : "Created by:"}</strong>{" "}
                                {actor?.name || "—"}
                              </Typography>
                            );
                          })()}
                          {excelS3Url ? (
                            <Typography variant="body2" color="textSecondary">
                              <strong>Excel:</strong>{" "}
                              <a
                                href={excelS3Url}
                                target="_blank"
                                rel="noopener noreferrer"
                                style={{ color: "#F47E46", textDecoration: "underline" }}
                              >
                                {excelFileName || "Open file"}
                              </a>
                            </Typography>
                          ) : null}
                          <Typography variant="body2" color="textSecondary">
                            <strong>Created at:</strong> {formatDateTime(createdAt)}
                          </Typography>
                          <Typography variant="body2" color="textSecondary">
                            <strong>Updated at:</strong> {formatDateTime(updatedAt)}
                          </Typography>
                        </Box>
                      </Box>
                    </Collapse>
                  </Paper>
                );
              })}
            </Box>
          )}
        </>
    </div>
  );
}

const mapStateToProps = (state) => ({
  user: state.auth?.user || null,
});

export default connect(mapStateToProps, { setLoading, setSnackInfo })(
  HrRacksheetActivityLogDashboard
);
